import { useEffect, useState } from "react";
import { Search, Loader2, MapPin, EyeOff, Eye, Check, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

type Result = {
  display_name: string;
  lat: string;
  lon: string;
  address?: { city?: string; town?: string; village?: string; state?: string; country?: string; country_code?: string };
};

type Existing = {
  city: string | null;
  country: string | null;
  country_code: string | null;
  region: string | null;
  lat: number | null;
  lng: number | null;
  is_hidden: boolean;
  source: string;
};

export function LocationEditor({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [existing, setExisting] = useState<Existing | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("community_member_locations")
        .select("city,country,country_code,region,lat,lng,is_hidden,source")
        .eq("user_id", user.id)
        .maybeSingle();
      setExisting((data as any) ?? null);
      setLoading(false);
    })();
  }, [user]);

  const search = async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=8&featuretype=city`,
        { headers: { "Accept-Language": "en" } },
      );
      const data = await res.json();
      setResults(data || []);
    } catch {
      // silent on debounce errors
    } finally {
      setSearching(false);
    }
  };

  // Debounced autocomplete as the user types
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
  }, [query]);

  const pick = async (r: Result) => {
    if (!user) return;
    setSaving(true);
    const a = r.address || {};
    const city = a.city || a.town || a.village || a.state || r.display_name.split(",")[0];
    const payload = {
      user_id: user.id,
      city,
      country: a.country ?? null,
      country_code: (a.country_code ?? "").toUpperCase() || null,
      region: a.state ?? null,
      lat: Number(r.lat),
      lng: Number(r.lon),
      source: "manual",
      is_hidden: existing?.is_hidden ?? false,
    };
    const { error } = await supabase
      .from("community_member_locations")
      .upsert(payload, { onConflict: "user_id" });
    setSaving(false);
    if (error) {
      toast({ title: "Could not save location", description: error.message, variant: "destructive" });
      return;
    }
    setExisting(payload as any);
    setQuery("");
    setResults([]);
    toast({ title: "Location saved" });
  };

  const toggleHidden = async () => {
    if (!user || !existing) return;
    const { error } = await supabase
      .from("community_member_locations")
      .update({ is_hidden: !existing.is_hidden })
      .eq("user_id", user.id);
    if (error) {
      toast({ title: "Could not update", description: error.message, variant: "destructive" });
      return;
    }
    setExisting({ ...existing, is_hidden: !existing.is_hidden });
  };

  const remove = async () => {
    if (!user) return;
    const { error } = await supabase
      .from("community_member_locations")
      .delete()
      .eq("user_id", user.id);
    if (error) {
      toast({ title: "Could not remove", description: error.message, variant: "destructive" });
      return;
    }
    setExisting(null);
  };

  if (loading) {
    return <div className="text-xs text-muted-foreground font-mono">Loading location...</div>;
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      {existing && (existing.city || existing.country) && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border/40 bg-card/40 text-xs font-mono">
            <MapPin className="w-3.5 h-3.5 text-accent" />
            <span className="text-foreground">
              {[existing.city, existing.country].filter(Boolean).join(", ")}
            </span>
            <span className="text-muted-foreground">
              ({existing.source})
            </span>
          </div>
          <button
            onClick={toggleHidden}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border/40 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors"
          >
            {existing.is_hidden ? <><Eye className="w-3 h-3" /> show on map</> : <><EyeOff className="w-3 h-3" /> hide from map</>}
          </button>
          <button
            onClick={remove}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border/40 text-xs font-mono text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 className="w-3 h-3" /> remove
          </button>
        </div>
      )}

      <div>
        <label className="text-xs font-mono text-muted-foreground mb-1.5 block">
          {existing ? "change city" : "set your city"}
        </label>
        <div className="relative">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="start typing... e.g. Mum"
            className="pr-9"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </div>
        </div>
        <p className="text-[10px] font-mono text-muted-foreground/70 mt-1.5">
          Powered by OpenStreetMap. Suggestions appear as you type. Only your city is shown publicly.
        </p>
      </div>

      {results.length > 0 && (
        <div className="space-y-1.5">
          {results.map((r, i) => (
            <button
              key={i}
              onClick={() => pick(r)}
              disabled={saving}
              className="w-full text-left px-3 py-2 rounded-lg border border-border/30 bg-card/20 hover:border-accent/40 hover:bg-card/40 text-sm text-foreground transition-colors disabled:opacity-50 inline-flex items-center justify-between gap-2"
            >
              <span className="truncate">{r.display_name}</span>
              <Check className="w-4 h-4 text-accent shrink-0 opacity-0 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}